import { useEffect, useState } from "react";
import { fetchAllPrograms, fetchProgram } from "./programsAPI";
import {
  CreateProgramInput,
  CreateProgramMembershipInput,
  UpdateProgramInput,
  Program,
  ProgramMembership
} from "../../API";
import { QueryStatus } from "../../app/types";
import {
  createProgram as createProgramAPI,
  createProgramMembership as createProgramMembershipAPI,
  updateProgram as updateProgramAPI
} from "./programsAPI";
import { useDispatch } from "react-redux";
import {
  selectPrograms,
  selectProgramsError,
  selectProgramsStatus,
  setPrograms,
  setProgramsError,
  setProgramsStatus
} from "./programsSlice";
import { useAppSelector } from "../../app/hooks";

export const useProgramById = (id?: string) => {
  const [program, setProgram] = useState<Program | null>();
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>();

  const getProgram = async (id: string): Promise<Program | null> => {
    setStatus("loading");
    setError(null);

    try {
      const result = await fetchProgram(id);
      setProgram(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      console.error("error:", error);
      setStatus("failed");
      setError(`Error loading programs: ${error}`);
    }
    return null;
  };

  useEffect(() => {
    if (id) {
      getProgram(id);
    }
  }, [id]);

  return {
    program,
    status,
    error,
    getProgram
  };
};

export const useAllPrograms = () => {
  const allPrograms = useAppSelector(selectPrograms);
  const status = useAppSelector(selectProgramsStatus);
  const error = useAppSelector(selectProgramsError);

  const dispatch = useDispatch();

  const getAllPrograms = async () => {
    // If already loaded or currently loading, don't reload
    if (status === "loading" || status === "succeeded") {
      return;
    }

    dispatch(setProgramsStatus("loading"));
    dispatch(setProgramsError(undefined));

    try {
      const results = await fetchAllPrograms();
      dispatch(setPrograms(results));
      dispatch(setProgramsStatus("succeeded"));
    } catch (error) {
      console.error("Error loading programs:", error);
      dispatch(setProgramsStatus("failed"));
      dispatch(setProgramsError(`Error loading programs: ${error}`));
    }
  };

  return {
    programs: allPrograms,
    status,
    error,
    getAllPrograms
  };
};

export const useCreateProgram = () => {
  const [newProgram, setNewProgram] = useState<Program | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const createProgram = async (input: CreateProgramInput) => {
    setStatus("loading");
    setError(null);

    try {
      const creationResult = await createProgramAPI(input);
      setNewProgram(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    newProgram,
    status,
    error,
    createProgram
  };
};

export const useUpdateProgram = () => {
  const [updatedProgram, setUpdatedProgram] = useState<Program | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const updateProgram = async (input: UpdateProgramInput) => {
    setStatus("loading");
    setError(null);

    try {
      const result = await updateProgramAPI(input);
      setUpdatedProgram(result);
      setStatus("succeeded");
      return result;
    } catch (error) {
      const errorString = `${error}`;
      console.error(errorString);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    updatedProgram,
    status,
    error,
    updateProgram
  };
};

export const useCreateProgramMembership = () => {
  const [newProgramMembership, setNewProgramMembership] =
    useState<ProgramMembership | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const createProgramMembership = async (
    input: CreateProgramMembershipInput
  ) => {
    setStatus("loading");
    setError(null);
    try {
      const creationResult = await createProgramMembershipAPI(input);
      setNewProgramMembership(creationResult);
      setStatus("succeeded");
      return creationResult;
    } catch (error) {
      const errorString = `${error}`;
      console.error("error creating program membership:", error);
      setStatus("failed");
      setError(errorString);
    }
    return null;
  };

  return {
    newProgramMembership,
    status,
    error,
    createProgramMembership
  };
};

/**
 *
 * @param programs - The programs to sort
 * @param sortByInstitution - a boolean that, if true, will first sort by Institution name and then Program name
 * @returns
 */
export const alphabetizePrograms = (
  programs: Program[],
  sortByInstitution = false
) => {
  return [...programs].sort((a, b) => {
    const institutionA = a.institution?.name?.toLowerCase();
    const institutionB = b.institution?.name?.toLowerCase();
    const programNameA = a.name?.toLowerCase();
    const programNameB = b.name?.toLowerCase();
    if (programNameA && programNameB && institutionA && institutionB) {
      if (!sortByInstitution || institutionA === institutionB) {
        if (programNameA < programNameB) {
          return -1;
        }
        if (programNameA > programNameB) {
          return 1;
        }
      } else if (institutionA > institutionB) {
        return 1;
      } else if (institutionA < institutionB) {
        return -1;
      }
    }
    return 0;
  });
};

export const ProgramFormErrors = {
  EmptyProgramName: "Please enter a value for the program name.",
  NoInstitution:
    "There is no institution currently selected. Please select an institution to use or contact your administrator."
};

export const ProgramNetworkErrors = {
  FetchAllProgramsError:
    "Error fetching programs. Please try again or contact your administrator.",
  FetchProgramError:
    "Error fetching program. Please try again or contact your administrator.",
  ProgramNotFound: "Program not found!",
  CreateProgramError:
    "There was an error creating this program. Please try again or contact your administrator.",
  CreateProgramMembershipError:
    "There was an error creating the program membership. Please try again or contact your administrator."
};

export const ProgramNotifications = {
  NoPrograms: "No programs to display.",
  NoProgramsInInstitution: "The selected institution has no programs."
};
